import * as ngCore from '@angular/core'
import * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'

import * as display from '../../display'
import * as me from '../../me'
import * as relay from '../../relay'
import * as variableOverrides from '../../variable-overrides'

import { getActionsConfig } from './actions'
import * as effects from './effects'
import { Selectors } from './selectors'
import { State } from './state'

/**
 * Route-bound scenario selector state for the active portal app.
 *
 * This feature intentionally reads the user's already-loaded app context from
 * {@link me.Feature}, but it does not write scenario list refreshes back into
 * {@link me.Feature}. `me` is the root navigation/session snapshot; changing it
 * causes app, scenario, and context selectors to emit across the application.
 *
 * The dropdown needs fresher scenario rows than `me` can safely provide after
 * create/update/delete operations, so this route-bound feature owns a short
 * lived list cache for the active app injector. That cache is destroyed when
 * the route-bound app branch is destroyed and recreated when the user enters a
 * new app.
 */
@ngCore.Injectable()
export class Feature extends store.Feature<
  State,
  Selectors,
  ReturnType<typeof getActionsConfig>
> {
  constructor() {
    const meFeature = ngCore.inject(me.Feature)
    const processesFeature = ngCore.inject(relay.processes.Feature)
    const routerFeature = ngCore.inject(router.Router)
    const toasterFeature = ngCore.inject(display.toaster.Feature)
    const variableOverridesFeature = ngCore.inject(variableOverrides.Feature)

    super({
      actions: getActionsConfig(),
      initialState: State.INITIAL,
      name: 'RouteBoundScenarios',
      selectors: () => new Selectors(this, meFeature, routerFeature),
    })

    this.registerEffects(
      effects.focusVariablesOnCreate(meFeature, variableOverridesFeature),
      effects.focusVariablesOnEdit(meFeature, variableOverridesFeature),
      effects.clearFocusedVariablesOnEditClose(variableOverridesFeature),
      effects.refreshScenariosOnOverlayDisplay(),
      effects.refreshScenariosOnRequest(meFeature),
      effects.submitEditOnRequest(meFeature, variableOverridesFeature),
      effects.submitDeleteOnRequest(meFeature),
      effects.moveToVisibilityOnRequest(meFeature),
      effects.showProcessToastOnCreateSuccess(
        toasterFeature,
        processesFeature,
      ),
      effects.showErrorToastOnMutationError(toasterFeature),
    )
  }
}
